import { faCaretDown, faCaretUp, faGear, faLock } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Grid, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import React, { useContext } from "react";
import { RateInterval } from "./EmployeeCostSection";
import { Container } from "./styles";
import { LegislationSocialSecurityCostSectionRef } from "../../../../models/legislation-social-security-cost/legislation-social-security-cost-section-ref";
import { ProformaEmployeeCostReportSectionRef } from "../../../../models/proforma-employee-cost-report/proforma-employee-cost-report-section-ref";
import SimplePopover from "../../../../reusable-components/SimplePopover/SimplePopover";
import { TextFieldTypeAmount } from "../../../../reusable-components/Table/AddEditItem/TextFieldTypeAmount/TextFieldTypeAmount";
import { ProformaEmployeeCostReportContext } from "../ProformaEmployeeCostReport";
import EmployeeCostSectionRateCellHint from "./EmployeeCostSectionRateCellHint";

interface Props {
  currentSection: ProformaEmployeeCostReportSectionRef;
  socialSecuritySection: LegislationSocialSecurityCostSectionRef | null;
  handleUpdateSection: (updatedSection: ProformaEmployeeCostReportSectionRef) => void;
  proportionalTo: ProformaEmployeeCostReportSectionRef | null;
  socialSecurityRates?: RateInterval[];
}

const EmployeeCostSectionRateCell = ({
  currentSection,
  socialSecuritySection,
  handleUpdateSection,
  proportionalTo,
  socialSecurityRates,
}: Props) => {
  const { viewMode, setReportUpdated } = useContext(ProformaEmployeeCostReportContext);
  return (
    <TableCell>
      <Container>
        {!currentSection.type?.considerIncomeBrackets && (
          <EmployeeCostSectionRateCellHint
            currentSection={currentSection}
            socialSecuritySection={socialSecuritySection}
            handleUpdateSection={handleUpdateSection}
            proportionalTo={proportionalTo}
          />
        )}
        {currentSection.type?.requiresInputRate ? (
          <Tooltip title={`Proportional to ${proportionalTo?.type?.name}`}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <TextFieldTypeAmount
                amountValue={currentSection.manualRate ?? currentSection.systemRate ?? 0}
                field="rate"
                labelKey=""
                handleChange={(_, number) => {
                  setReportUpdated(true);
                  handleUpdateSection({
                    ...currentSection,
                    manualRate: number ?? 0,
                  });
                }}
                disabled={viewMode}
                sxProps={{
                  width: "150px",
                  "& .MuiCircularProgress-root": {
                    display: "none",
                  },
                  "& .MuiInputBase-root": {
                    paddingRight: "0px",
                    borderRadius: "8px",
                  },
                  "& .MuiInputBase-input": {
                    padding: "6px",
                    textAlign: "end",
                  },
                }}
              />
              <span>%</span>
            </div>
          </Tooltip>
        ) : socialSecurityRates ? (
          <SimplePopover
            buttonType="icon"
            buttonContent={<FontAwesomeIcon icon={faGear} />}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="center">From</TableCell>
                  <TableCell align="center">To</TableCell>
                  <TableCell align="center">Rate</TableCell>
                  <TableCell align="center">
                    <Typography>Calculated amount</Typography>
                    <Typography variant="body2"> (Not considering min and cap amount)</Typography>
                  </TableCell>
                  <TableCell align="center">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {socialSecurityRates.map((rateInterval, key) => (
                  <TableRow key={key}>
                    <TableCell align="right">{rateInterval.from}</TableCell>
                    <TableCell align="right">{rateInterval.to}</TableCell>
                    <TableCell align="center">{rateInterval.rate}</TableCell>
                    <TableCell align="right">{rateInterval.rawAmount}</TableCell>
                    <TableCell align="right">
                      <Grid style={{ display: "flex", justifyContent: "end", alignItems: "center" }}>
                        <Typography>{rateInterval.amount}</Typography>
                        {(rateInterval.capAmount > 0 || rateInterval.minAmount > 0 || rateInterval.fixedAmount > 0) && (
                          <Grid>
                            {rateInterval.capAmount > 0 && (
                              <Tooltip title={`Cap amount : ${rateInterval.capAmount}`}>
                                <span>
                                  <FontAwesomeIcon icon={faCaretUp} />
                                </span>
                              </Tooltip>
                            )}
                            {rateInterval.minAmount > 0 && (
                              <Tooltip title={`Min amount : ${rateInterval.minAmount}`}>
                                <span>
                                  <FontAwesomeIcon icon={faCaretDown} />
                                </span>
                              </Tooltip>
                            )}
                            {rateInterval.fixedAmount > 0 && (
                              <Tooltip title={`Fixed amount : ${rateInterval.fixedAmount}`}>
                                <span>
                                  <FontAwesomeIcon
                                    icon={faLock}
                                    style={{ fontSize: "12px", color: "grey" }}
                                  />
                                </span>
                              </Tooltip>
                            )}
                          </Grid>
                        )}
                      </Grid>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </SimplePopover>
        ) : currentSection.systemRate ? (
          <Tooltip title={`Default rate proportional to ${proportionalTo?.type?.name}`}>
            <Typography variant="body2">{`${currentSection.systemRate} %`}</Typography>
          </Tooltip>
        ) : (
          ""
        )}
        <Typography variant="body2">
          {!!currentSection.type?.dividedBy && currentSection.type.dividedBy > 0 && (
            <Tooltip title={`Divided by ${currentSection.type.dividedBy}`}>
              <span>{` / ${currentSection.type.dividedBy}`}</span>
            </Tooltip>
          )}
          {!!currentSection.type?.multipliedBy && currentSection.type.multipliedBy > 0 && (
            <Tooltip title={`Multiplied by ${currentSection.type.dividedBy}`}>
              <span>{` * ${currentSection.type.multipliedBy}`}</span>
            </Tooltip>
          )}
        </Typography>
      </Container>
    </TableCell>
  );
};
export default EmployeeCostSectionRateCell;
